import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';

import styled from 'styled-components';
import { SkillIcon } from 'atoms';
import { Colors, LanguageLevelOptions } from 'context';
import { monthNameToNumber } from 'core/hooks/CareerProfile/transform/monthBridge';
import { ICareerProfileFormValues } from 'organisms/CareerProfileForm/types';
import fontService, { FontWeight, TextType } from 'services/FontService';
import { desktop, mobile } from 'services/ui';

/**
 * Витрина профиля — «как меня видят» (ТЗ 0039, доводка UX).
 *
 * До неё профиль существовал ТОЛЬКО как форма: двадцать два поля ввода, и
 * посмотреть на своё резюме целиком было негде. Владелец сказал об этом прямо:
 * «сложно посмотреть собственный профиль, хочется видеть себя». Скачанный PDF
 * этого не решает — он вне экрана и требует отдельного действия.
 *
 * 🔴 Показываем ТО ЖЕ, что уходит работодателю, и по тем же правилам. Скрытые
 * навыки здесь не видны, потому что их нет и в резюме: витрина, показывающая
 * больше настоящего резюме, обманывает в самую неудобную сторону — человек
 * думает, что навык виден, а его там нет.
 *
 * Пустые блоки не рисуем вовсе. Скелет из подписей «Опыт — не указан, Проекты
 * — не указаны» выглядит как список претензий; чего не заполнено, то видно по
 * шкале заполненности рядом.
 */
export interface ICareerProfileShowcase {
  values: ICareerProfileFormValues;
  photoUrl: string;
  /** Текст «о себе» на языке резюме. Пусто — блок не рисуется. */
  headline?: string;
  about?: string;
}

/**
 * `03.2021` — как в PDF (`cv_render.py`), а не `March.2021`.
 *
 * 🔴 `date.month` в форме — английское НАЗВАНИЕ месяца (`monthNumberToName`),
 * а не число. Склейка через точку давала `January.2021`, причём в польской
 * версии по-английски. Витрина обязана совпадать с тем, что уходит
 * работодателю в PDF.
 */
const monthYear = (date: { year: string; month: string }): string => {
  const month = monthNameToNumber(date.month);
  const mm = month ? String(month).padStart(2, '0') : '';
  return [mm, date.year].filter(Boolean).join('.');
};

/** Дата вида `2025-09-01` → `01.09.2025`. Пустую и нераспознанную не трогаем. */
const humanDate = (value: string): string => {
  const parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value || '');
  return parts ? `${parts[3]}.${parts[2]}.${parts[1]}` : value || '';
};

/** Подпись уровня языка вместо кода перечисления (`NATIVE`, `B2`). */
const levelLabel = (level: string): string =>
  LanguageLevelOptions.find((option) => option.value === level)?.label ?? level ?? '';

const period = (
  from: { year: string; month: string },
  to: { year: string; month: string },
  current: boolean,
  now: string,
): string => [monthYear(from), current ? now : monthYear(to)].filter(Boolean).join(' — ');

export const CareerProfileShowcase = memo(
  ({ values, photoUrl, headline, about }: ICareerProfileShowcase) => {
    const { t } = useTranslation();

    const fullName = [values.firstName, values.lastName].filter(Boolean).join(' ');
    const place = [values.city, values.country].filter(Boolean).join(', ');
    // Скрытые навыки не показываем — их нет и в резюме (см. докстринг).
    // Порядок тот же, что в PDF: подтверждённые школой впереди, иначе витрина
    // и скачанный файл перечисляют одно и то же по-разному.
    const skills = values.skills
      .filter((skill) => !skill.isHidden)
      .slice()
      .sort((a, b) => Number(b.source === 'course') - Number(a.source === 'course'));
    const links = [
      { key: 'github', url: values.githubUrl },
      { key: 'linkedin', url: values.linkedinUrl },
      { key: 'portfolio', url: values.portfolioUrl },
    ].filter((link) => Boolean(link.url));

    return (
      <Sheet>
        <Top>
          {photoUrl ? <Photo src={photoUrl} alt="" /> : <PhotoStub aria-hidden />}
          <TopText>
            <FullName>{fullName || t('careerProfile.showcase.noName')}</FullName>
            {values.desiredPosition ? <Position>{values.desiredPosition}</Position> : null}
            {/* 🔴 Контакты и право на работу показываем, потому что они ЕСТЬ в
                PDF, который уходит работодателю. Это профиль самого студента,
                утечки нет; а «увидеть себя глазами работодателя» без телефона
                и без права на работу задачу не решает — именно эти две строки
                польский наниматель ищет глазами первыми. */}
            <Facts>
              {values.phone ? <Fact>{values.phone}</Fact> : null}
              {values.email ? <Fact>{values.email}</Fact> : null}
            </Facts>
            <Facts>
              {values.seniority ? (
                <Fact>
                  {t(`careerProfile.seniority.${values.seniority}`, {
                    defaultValue: values.seniority,
                  })}
                </Fact>
              ) : null}
              {place ? <Fact>{place}</Fact> : null}
              {values.workMode ? (
                <Fact>
                  {t(`careerProfile.workMode.${values.workMode}`, {
                    defaultValue: values.workMode,
                  })}
                </Fact>
              ) : null}
              {values.relocationReady ? (
                <Fact>{t('careerProfile.showcase.relocation')}</Fact>
              ) : null}
              {values.availableFrom ? (
                <Fact>
                  {t('careerProfile.showcase.availableFrom', {
                    date: humanDate(values.availableFrom),
                  })}
                </Fact>
              ) : null}
              {values.workPermit ? (
                <Fact>
                  {t('careerProfile.showcase.workPermit', {
                    value: values.workPermit,
                  })}
                </Fact>
              ) : null}
            </Facts>
            {links.length ? (
              <Links>
                {links.map((link) => (
                  <Link key={link.key} href={link.url} target="_blank" rel="noopener noreferrer">
                    {t(`careerProfile.showcase.link_${link.key}`)}
                  </Link>
                ))}
              </Links>
            ) : null}
          </TopText>
        </Top>

        {headline || about ? (
          <Section>
            <SectionTitle>{t('careerProfile.showcase.about')}</SectionTitle>
            {headline ? <Headline>{headline}</Headline> : null}
            {about ? <Paragraph>{about}</Paragraph> : null}
          </Section>
        ) : null}

        {skills.length || values.customSkills.length ? (
          <Section>
            <SectionTitle>{t('careerProfile.skills.title')}</SectionTitle>
            <Chips>
              {skills.map((skill) => (
                <Chip key={skill.skillId}>
                  <SkillIcon
                    slug={skill.slug}
                    name={skill.name}
                    category={skill.category}
                    iconUrl={skill.iconUrl}
                    size={18}
                  />
                  {skill.name}
                  {skill.source === 'course' ? (
                    <Verified>{t('careerProfile.skills.verifiedShort')}</Verified>
                  ) : null}
                </Chip>
              ))}
              {/* Свободный текст — тоже навыки, но справочником не покрытые:
                  значка у них быть не может, только буква по умолчанию. */}
              {values.customSkills.map((name) => (
                <Chip key={`custom-${name}`}>{name}</Chip>
              ))}
            </Chips>
          </Section>
        ) : null}

        {values.experience.length ? (
          <Section>
            <SectionTitle>{t('careerProfile.showcase.experience')}</SectionTitle>
            {values.experience.map((row) => (
              <Entry key={row.id}>
                <EntryHead>
                  <EntryTitle>{row.position}</EntryTitle>
                  <EntryPeriod>
                    {period(
                      row.startDate,
                      row.endDate,
                      row.isCurrent,
                      t('careerProfile.showcase.now'),
                    )}
                  </EntryPeriod>
                </EntryHead>
                {row.company ? <EntrySub>{row.company}</EntrySub> : null}
                {row.description ? <Paragraph>{row.description}</Paragraph> : null}
              </Entry>
            ))}
          </Section>
        ) : null}

        {values.projects.length ? (
          <Section>
            <SectionTitle>{t('careerProfile.projects.title')}</SectionTitle>
            {values.projects.map((project) => (
              <Entry key={project.id}>
                <EntryHead>
                  <EntryTitle>{project.title}</EntryTitle>
                </EntryHead>
                {project.role ? <EntrySub>{project.role}</EntrySub> : null}
                {project.description ? <Paragraph>{project.description}</Paragraph> : null}
                {project.technologies.length ? (
                  <Chips>
                    {project.technologies.map((tech) => (
                      <Chip key={tech.id}>{tech.name}</Chip>
                    ))}
                  </Chips>
                ) : null}
                <Links>
                  {project.repoUrl ? (
                    <Link href={project.repoUrl} target="_blank" rel="noopener noreferrer">
                      {t('careerProfile.showcase.repo')}
                    </Link>
                  ) : null}
                  {project.demoUrl ? (
                    <Link href={project.demoUrl} target="_blank" rel="noopener noreferrer">
                      {t('careerProfile.showcase.demo')}
                    </Link>
                  ) : null}
                </Links>
              </Entry>
            ))}
          </Section>
        ) : null}

        {values.education.length ? (
          <Section>
            <SectionTitle>{t('careerProfile.showcase.education')}</SectionTitle>
            {values.education.map((row) => (
              <Entry key={row.id}>
                <EntryHead>
                  <EntryTitle>{row.institution}</EntryTitle>
                  <EntryPeriod>{period(row.startDate, row.endDate, false, '')}</EntryPeriod>
                </EntryHead>
                {row.specialization || row.degree ? (
                  <EntrySub>{[row.degree, row.specialization].filter(Boolean).join(', ')}</EntrySub>
                ) : null}
              </Entry>
            ))}
          </Section>
        ) : null}

        {values.languages.length ? (
          <Section>
            <SectionTitle>{t('careerProfile.showcase.languages')}</SectionTitle>
            <Chips>
              {values.languages.map((row) => (
                <Chip key={row.id}>
                  {row.language}
                  {row.level ? <Verified>{levelLabel(row.level)}</Verified> : null}
                </Chip>
              ))}
            </Chips>
          </Section>
        ) : null}

        {values.certificates.length ? (
          <Section>
            <SectionTitle>{t('careerProfile.certificates.title')}</SectionTitle>
            {values.certificates.map((row) => (
              <Entry key={row.id}>
                <EntryHead>
                  <EntryTitle>{row.title}</EntryTitle>
                  {row.issuedAt ? <EntryPeriod>{humanDate(row.issuedAt)}</EntryPeriod> : null}
                </EntryHead>
                {row.issuer ? <EntrySub>{row.issuer}</EntrySub> : null}
              </Entry>
            ))}
          </Section>
        ) : null}
      </Sheet>
    );
  },
);

const Sheet = styled.article`
  background: ${Colors.white};
  border: 1px solid ${Colors.gray};
  border-radius: 12px;
  padding: 20px;

  ${desktop} {
    padding: 32px;
  }
`;

const Top = styled.header`
  display: flex;
  gap: 20px;
  align-items: flex-start;
  margin-bottom: 22px;

  ${mobile} {
    flex-direction: column;
    gap: 12px;
  }
`;

const Photo = styled.img`
  width: 96px;
  height: 96px;
  border-radius: 12px;
  object-fit: cover;
  flex: none;
`;

const PhotoStub = styled.div`
  width: 96px;
  height: 96px;
  border-radius: 12px;
  flex: none;
  background: ${Colors.bg_gray};
`;

const TopText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
`;

const FullName = styled.h2`
  margin: 0;
  ${fontService.h3()}
`;

const Position = styled.div`
  /* Жёлтый как ЦВЕТ ТЕКСТА даёт 1.33:1 на белом при норме 4.5:1 —
     правило и причина в context/constants/colors.ts. */
  color: ${Colors.black};
  ${fontService.text({ type: TextType.medium, weight: FontWeight.medium })}
`;

const Facts = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px 14px;
`;

const Fact = styled.span`
  color: ${Colors.gray_dark};
  ${fontService.text({ type: TextType.small })}
`;

const Links = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
`;

const Link = styled.a`
  /* Жёлтый как ЦВЕТ ТЕКСТА даёт 1.33:1 на белом при норме 4.5:1 —
     правило и причина в context/constants/colors.ts. */
  color: ${Colors.black};
  ${fontService.text({ type: TextType.small, weight: FontWeight.medium })}
`;

const Section = styled.section`
  margin-bottom: 22px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h3`
  margin: 0 0 10px;
  ${fontService.text({ type: TextType.medium, weight: FontWeight.bold })}
`;

const Headline = styled.div`
  margin-bottom: 6px;
  ${fontService.text({ type: TextType.small, weight: FontWeight.medium })}
`;

const Paragraph = styled.p`
  margin: 0 0 6px;
  white-space: pre-line;
  ${fontService.text({ type: TextType.small })}
`;

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 10px;
  border-radius: 999px;
  background: ${Colors.bg_gray};
  ${fontService.text({ type: TextType.small })}
`;

const Verified = styled.span`
  color: ${Colors.gray_dark};
  ${fontService.text({ type: TextType.extraSmall })}
`;

const Entry = styled.div`
  margin-bottom: 14px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const EntryHead = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: baseline;
`;

const EntryTitle = styled.span`
  ${fontService.text({ type: TextType.small, weight: FontWeight.bold })}
`;

const EntryPeriod = styled.span`
  color: ${Colors.gray_dark};
  white-space: nowrap;
  ${fontService.text({ type: TextType.extraSmall })}
`;

const EntrySub = styled.div`
  color: ${Colors.gray_dark};
  margin-bottom: 4px;
  ${fontService.text({ type: TextType.small })}
`;
